import { useState } from "react";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./_Shell";
import { getOrCreateOnboarding, saveOnboarding } from "../../lib/onboarding";
import { getDefaults } from "../../lib/businessDefaults";
import { cardBorderClassName } from "../../lib/cardStyles";

export default function OnboardingStep3() {
  const navigate = useNavigate();
  const ob = getOrCreateOnboarding();
  const defaults = getDefaults(
    (ob as Record<string, unknown>).businessType as string ?? "other"
  );

  const [aiName, setAiName] = useState(
    (ob.step3 as Record<string, unknown>)?.aiName as string ?? defaults.aiName
  );
  const [greeting, setGreeting] = useState(
    (ob.step3 as Record<string, unknown>)?.greeting as string ?? defaults.greeting
  );

  const canContinue = aiName.trim().length > 0 && greeting.trim().length > 0;

  function handleContinue() {
    saveOnboarding({
      ...ob,
      step3: { aiName, greeting } as Parameters<typeof saveOnboarding>[0]["step3"],
    });
    navigate("/onboarding/checkout");
  }

  return (
    <OnboardingShell
      step={3}
      title="Meet your AI receptionist"
      subtitle="Here's what your AI will say on its first call. Edit anything you like."
    >
      <div className="grid gap-5">
        <div>
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-purple-200"
            style={{
              background: "rgba(138, 43, 226, 0.2)",
              border: "1px solid rgba(138, 43, 226, 0.35)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            <i className={`${defaults.icon} text-purple-400`} />
            {defaults.label}
          </span>
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-white/85 mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Your AI's name *
          </label>
          <input
            type="text"
            value={aiName}
            onChange={(e) => setAiName(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3 text-sm text-white outline-none focus:border-white/25"
          />
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-white/85 mb-2"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Opening greeting *
          </label>
          <textarea
            rows={4}
            value={greeting}
            onChange={(e) => setGreeting(e.target.value)}
            className="w-full rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3 text-sm text-white outline-none focus:border-white/25 resize-y"
          />
        </div>

        <div>
          <p
            className="text-xs text-white/50 mb-2 uppercase tracking-wide"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            AI will also know:
          </p>
          <div
            className={`rounded-xl px-4 py-3 text-sm text-gray-300 ${cardBorderClassName}`}
            style={{
              background: "rgba(13, 13, 18, 0.7)",
              fontFamily: "'Space Grotesk', sans-serif",
              lineHeight: "1.6",
            }}
          >
            {defaults.knowledgeStarter}
          </div>
        </div>

        <div className="flex gap-3 mt-1">
          <button
            type="button"
            onClick={() => navigate("/onboarding/2")}
            className="w-full rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/90 hover:border-white/30 cursor-pointer"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canContinue}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition-opacity disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            style={{
              background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            Continue to payment →
          </button>
        </div>
      </div>
    </OnboardingShell>
  );
}
